import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Layout, message, Tooltip } from 'antd';
import { claimBox, releaseBox } from '../../collab/useEditorCollab';
import { CONFIGURATOR_LOCK_ID } from '../../collab/lockIds';
import { anyRemoteLockHolder } from '../../collab/structureLocks';
import './Navbar.less';
import { ReactComponent as SmartLogo } from '../../images/logo.svg';

import { RightPropertiesComponent } from './RightProperties';
import { UserInfosComponent } from './UserInfos';
import { HelpButton } from './HelpButton';
import { PresenceBar } from '../../collab/PresenceBar';
import { HistoryPanel } from '../../history/HistoryPanel';
import { UndoRedoButtons } from '../../undo/UndoRedoButtons';
import { useStore } from '../../store/store';
import ZoomControl from './ZoomControl';
import { navbarHeight } from '../../constants/gridConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { useModalOpener } from '../../hooks';
import { isAImgBox, isATitleBox, isATextBox, isAVariableBox } from '../../utils/boxes';
import { useIsFormView, useOverlay } from '../../contexts/OverlayContext';
import { useExitFormView } from '../LeftSide/useExitFormView';
import { getPrimaryDark, PANEL_LIGHT_BG } from '../../constants/constants';
import { NavbarButton } from './NavbarButton';

// Précalculés ici (nom en literal, pas de ternaire dans icon()) : la macro
// FontAwesome exige des string literals, elle ne résout pas un style choisi
// dynamiquement.
const sidebarIconLight = icon({ name: 'sidebar', style: 'light' });
const sidebarIconSolid = icon({ name: 'sidebar', style: 'solid' });
const sidebarFlipIconLight = icon({ name: 'sidebar-flip', style: 'light' });
const sidebarFlipIconSolid = icon({ name: 'sidebar-flip', style: 'solid' });
const fileIconLight = icon({ name: 'file', style: 'light' });

const selector = ({
  selectedBoxId,
  boxes,
  masks,
  user,
  groupSelection,
  proposalList,
  maskEditing,
  setMaskEditing,
  currentDocument,
  containers,
  currentProposal,
  currentTemplate,
  documentIdentity,
  landscape,
}) => {
  const box = [
    ...boxes,
    ...masks.map((m) => m.boxes).flat(),
  ].find((box) => selectedBoxId && box.id === selectedBoxId)
  const container = containers.find(container => container.columnsIds.includes(box?.columnId))
  let selectedBox = box
  if (!container?.editable && (isAImgBox(box?.type) || isAVariableBox(box?.type)) && container?.hasOwnProperty('editable') && currentProposal.id !== null) {
    selectedBox = {}
  }
  return {
    selectedBox: selectedBox,
    groupSelection,
    user,
    proposalList,
    maskEditing,
    setMaskEditing,
    currentDocument,
    currentTemplate,
    documentIdentity,
    landscape,
  }
};

const { Header } = Layout;

const navbarStyle = {
  display: 'flex',
  justifyContent: 'end',
  alignItems: 'center',
  margin: '0 auto',
  height: navbarHeight,
  width: '100%',
  padding: 0,
  position: 'fixed',
  zIndex: 1100,
}


// Valeur ellipsée d'une ligne d'identité (nom possiblement long borné, le reste
// coupé plutôt que de pousser sous les contrôles de zoom centrés).
const IdentityValue = ({ text, color, max = 320, muted = false }) => (
  <span style={{
    display: 'inline-block',
    maxWidth: max,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
    color,
    opacity: muted ? 0.75 : 1,
  }}>{text}</span>
);

// Identité du document en mode proposition : opportunité, offre et document,
// chacun sur sa propre ligne. L'empilement (plutôt qu'une ligne large) limite la
// largeur pour ne pas déborder sous les contrôles de zoom (centrés) sur petit
// écran ; trois lignes de 11px tiennent dans la navbar (54px). Le conteneur est
// borné en largeur (cf. identityColumnStyle) et chaque valeur s'ellipse.
const ProposalIdentity = ({ identity, color }) => {
  const { opportunityName, opportunityChrono, offerName, documentName } = identity;
  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    lineHeight: 1.15,
    color,
    fontSize: 11,
  };
  const tagStyle = { fontWeight: 700, color, flexShrink: 0 };
  return (
    <>
      {opportunityName && (
        <div style={rowStyle}>
          <span style={tagStyle}>Opportunité</span>
          <IdentityValue text={`"${opportunityName}"`} color={color} max={260} />
          {opportunityChrono && (
            <IdentityValue text={opportunityChrono} color={color} max={130} muted />
          )}
        </div>
      )}
      {offerName && (
        <div style={rowStyle}>
          {/* Tag jamais atténué : seules les valeurs le sont (ci-dessous), pour
              que les trois libellés gardent le même poids visuel. */}
          <span style={tagStyle}>Offre</span>
          <IdentityValue text={`"${offerName}"`} color={color} max={300} muted={!!opportunityName} />
        </div>
      )}
      {documentName && (
        <div style={rowStyle}>
          <span style={tagStyle}>Document</span>
          <IdentityValue text={`"${documentName}"`} color={color} max={300} muted />
        </div>
      )}
    </>
  );
};

// Identité complète, affichée au survol du bloc : sur petit écran les valeurs
// sont ellipsées.
const fullIdentityLines = (identity) => [
  identity.opportunityName &&
    `Opportunité "${identity.opportunityName}"${identity.opportunityChrono ? ` ${identity.opportunityChrono}` : ''}`,
  identity.offerName && `Offre "${identity.offerName}"`,
  identity.documentName && `Document "${identity.documentName}"`,
].filter(Boolean);

// Borne le conteneur de gauche (logo, toggles, nom, badge) juste avant le zoom,
// centré en absolute à 50vw : décalage gauche du conteneur (left 8 + marginLeft
// 15), demi-largeur du groupe zoom (143px, 177px avec le bouton main, cf.
// ZoomControl) et 16px d'espace. Le nom est le seul élément qui rétrécit.
const leftContainerMaxWidth = (withPanButton) =>
  `calc(50vw - ${23 + (withPanButton ? 89 : 72) + 16}px)`;

// Format du document, fixé à la création du modèle (la proposition en hérite).
// L'éditeur défile en continu et le zoom est recalé par orientation : la forme
// de la page ne suffit pas à savoir si on est en paysage. Style d'étiquette et
// non de bouton, le format ne se change pas ici.
const DocumentFormatBadge = ({ landscape, color }) => {
  const { t } = useTranslation();
  return (
    <Tooltip
      title={t(landscape ? 'document-format-landscape-hint' : 'document-format-portrait-hint')}
      placement='bottom'
    >
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        flexShrink: 0,
        height: 22,
        marginLeft: 12,
        padding: '0 8px',
        border: `1px solid ${color}33`,
        borderRadius: 11,
        backgroundColor: `${color}0d`,
        color,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        cursor: 'default',
      }}>
        <FontAwesomeIcon
          icon={fileIconLight}
          rotation={landscape ? 270 : undefined}
          flip={landscape ? undefined : 'horizontal'}
          style={{ fontSize: 12 }}
        />
        {t(landscape ? 'document-format-landscape' : 'document-format-portrait')}
      </span>
    </Tooltip>
  );
};

const Navbar = () => {
  const { overlayProps, handleOverlayCollapse } = useOverlay()
  const isFormView = useIsFormView();
  const exitFormView = useExitFormView();
  const {
    selectedBox,
    user,
    proposalList,
    maskEditing,
    setMaskEditing,
    currentDocument,
    currentTemplate,
    documentIdentity,
    landscape,
  } = useStore(selector);
  const { t } = useTranslation();
  const optionsRef = useRef();
  const headerLogoRef = useRef();
  const [configurationPanelVisible, setRawConfigurationPanelVisible] = useModalOpener(false)
  const [configurationTab, setConfigurationTab] = useState('Couleurs')

  // Collab : configurateur exclusif avec toute édition — il ne s'ouvre que si
  // personne n'édite (box, variable, section), et toute édition est refusée
  // pendant qu'il est ouvert (gardes dans handleSelectBox / variables)
  const remoteEditLock = useStore((state) => anyRemoteLockHolder(state))
  const showTemplateName = currentDocument.type === 'template' && currentTemplate?.name;
  const isProposalDoc = currentDocument.type === 'opportunity' || currentDocument.type === 'offer';
  // Bloc identité (mode proposition) : nom de l'opportunité/offre + nom du
  // document, pour distinguer concrètement le contexte au-delà de la couleur.
  const showProposalIdentity =
    isProposalDoc &&
    !proposalList &&
    !!documentIdentity &&
    !!(documentIdentity.opportunityName || documentIdentity.offerName || documentIdentity.documentName);
  const showDocumentFormat = !!showTemplateName || (isProposalDoc && !proposalList);
  const canScrollX = useStore(({ canScrollX }) => canScrollX);
  const templateKindLabel = currentTemplate?.pattern === 'section' ? 'Section' : 'Modèle';
  const identityTooltip = showTemplateName
    ? `${templateKindLabel} "${currentTemplate.name}"`
    : showProposalIdentity && fullIdentityLines(documentIdentity).map((line) => <div key={line}>{line}</div>);

  const setConfigurationPanelVisible = useCallback((visible) => {
    if (visible) {
      const holder = anyRemoteLockHolder(useStore.getState());
      if (holder) {
        message.warning(`Édition en cours par ${holder.name}`);
        return;
      }
      claimBox(CONFIGURATOR_LOCK_ID);
    } else {
      releaseBox(CONFIGURATOR_LOCK_ID);
    }
    setRawConfigurationPanelVisible(visible);
  }, [setRawConfigurationPanelVisible])

  // Claim perdu (lock_denied) ou édition d'un co-éditeur apparue pendant la
  // configuration (race résiduelle : le configurateur cède) : fermer
  useEffect(() => {
    if (configurationPanelVisible && remoteEditLock) {
      message.warning(`Édition en cours par ${remoteEditLock.name}`);
      releaseBox(CONFIGURATOR_LOCK_ID);
      setRawConfigurationPanelVisible(false);
    }
  }, [configurationPanelVisible, remoteEditLock, setRawConfigurationPanelVisible])

  // Demande d'ouverture du configurateur émise ailleurs (onglet "Paramètres"
  // du panel gauche) : un tick incrémenté dans le store. La Navbar reste le
  // seul point qui applique la logique collab (claim/garde) via
  // setConfigurationPanelVisible. Skip du montage initial (tick 0).
  const configuratorOpenRequestTick = useStore((state) => state.configuratorOpenRequestTick)
  const prevConfiguratorTickRef = useRef(configuratorOpenRequestTick)
  useEffect(() => {
    if (configuratorOpenRequestTick !== prevConfiguratorTickRef.current) {
      prevConfiguratorTickRef.current = configuratorOpenRequestTick;
      setConfigurationPanelVisible(true);
    }
  }, [configuratorOpenRequestTick, setConfigurationPanelVisible])

  useEffect(() => {
    function handleWindowResize() {
      // Le bouton « Retour au document » est plus large que le logo : masques
      // ou formulaire, c'est la même place prise.
      const headerLogoSize = (maskEditing?.length > 0 || isFormView) ? 190 : 138
      if (optionsRef.current.clientWidth > window.innerWidth - headerLogoSize) {
        headerLogoRef.current.style.display = 'none'
      } else {
        headerLogoRef.current.style.display = 'flex'
      }
    }
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [selectedBox?.id, maskEditing, isFormView]);

  // Fond blanc fixe (les deux modes) : la distinction visuelle
  // template/proposition ne vient plus du fond mais de navColor (logo,
  // nom du modèle, indicateur d'enregistrement) — inversion fond/texte.
  const navColor = getPrimaryDark(currentDocument.type === 'template');

  // Les deux vues qui prennent la place du document — masques et formulaire —
  // n'ont que le rail de gauche pour en sortir, et rien n'y dit qu'on regarde
  // autre chose que le document. Le bouton prend la place du logo, comme il le
  // faisait déjà pour les masques.
  // Ordre de priorité de Middle : le formulaire passe devant les masques, le
  // bouton défait donc ce qui est réellement affiché.
  const backToDocument = isFormView
    ? () => exitFormView()
    : maskEditing?.length > 0
      ? () => setMaskEditing([])
      : null;

  return (
    <Header style={{
      ...navbarStyle,
      backgroundColor: PANEL_LIGHT_BG,
      color: navColor,
      // Sépare la top bar du document, comme les panels gauche/droite
      // (cf. OverlaySider.jsx).
      borderBottom: '1px solid rgba(11, 34, 57, 0.25)',
    }}>
      <div className='navbar__logo-container' style={{ marginLeft: 15, position: 'absolute', left: 8, maxWidth: leftContainerMaxWidth(canScrollX) }} ref={headerLogoRef}>
        {backToDocument ?
          <Button
            onClick={backToDocument}
            icon={<FontAwesomeIcon icon={icon({ name: 'arrow-left', style: 'solid' })} style={{ marginRight: "8px" }} />}
            style={{ padding: '4px 10px', flexShrink: 0 }}
            type='primary'
          >
            <span>{t('main-document')}</span>
          </Button>
          :
          <>
            {user?.logo ?
              <div style={{ height: '100%', flexShrink: 0 }}>
                <img src={user.logo} alt="User Logo" className='navbar__logo' />
              </div>
              :
              <div style={{ width: 115, flexShrink: 0 }}>
                <SmartLogo className='navbar__logo' />
              </div>
            }
          </>
        }
        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, flexShrink: 0 }}>
          <NavbarButton
            tooltipContent={t('toggle-left-panel')}
            onClick={() => handleOverlayCollapse('left', !overlayProps.left.collapsed)}
            icon={<FontAwesomeIcon
              icon={overlayProps.left.collapsed ? sidebarIconLight : sidebarIconSolid}
              style={{ fontSize: 20, margin: 0, color: navColor }}
            />}
          />
          <NavbarButton
            tooltipContent={t('toggle-right-panel')}
            onClick={() => handleOverlayCollapse('right', !overlayProps.right.collapsed)}
            icon={<FontAwesomeIcon
              icon={overlayProps.right.collapsed ? sidebarFlipIconLight : sidebarFlipIconSolid}
              style={{ fontSize: 20, margin: 0, color: navColor }}
            />}
          />
        </div>
        {(showTemplateName || showProposalIdentity) && (
          // lineHeight explicite : neutralise le grand line-height hérité de
          // .ant-layout-header (~54px), sinon les deux lignes empilées
          // dépassent la navbar et la ligne titre (overflow:hidden, donc
          // min-height flex = 0) s'écrase à 0. Largeur : seul élément qui
          // rétrécit dans le conteneur de gauche (borné avant le zoom, cf.
          // leftContainerMaxWidth) ; le texte s'ellipse, le badge de format suit
          // le nom jusqu'au bord du zoom. 440px max sur grand écran.
          <Tooltip title={identityTooltip} placement='bottom'>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: '0 1 auto', minWidth: 0, marginLeft: 20, maxWidth: 440, overflow: 'hidden' }}>
              {showTemplateName && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                  lineHeight: 1.3,
                  color: navColor,
                  fontStyle: 'italic',
                  fontSize: 12,
                }}>
                  <span style={{ fontWeight: 700, color: navColor, flexShrink: 0 }}>
                    {templateKindLabel}
                  </span>
                  <span style={{
                    display: 'inline-block',
                    maxWidth: 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'bottom',
                  }}>"{currentTemplate?.name}"</span>
                </div>
              )}
              {showProposalIdentity && (
                <ProposalIdentity identity={documentIdentity} color={navColor} />
              )}
            </div>
          </Tooltip>
        )}
        {showDocumentFormat && (
          <DocumentFormatBadge landscape={landscape} color={navColor} />
        )}
      </div>
      <div style={{
        position: 'absolute',
        left: '50%',
        top: 0,
        height: '100%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
      }}>
        <ZoomControl />
      </div>
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginRight: 15,
      }} ref={optionsRef}>
        <div
          id='VariablePortal'
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        />
        <div
          id='ComponentPortal'
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
        </div>

        {/* -- RIGHT Properties Buttons */}
        {!proposalList && <RightPropertiesComponent
          configurationPanelVisible={configurationPanelVisible}
          setConfigurationPanelVisible={setConfigurationPanelVisible}
          configurationTab={configurationTab}
          setConfigurationTab={setConfigurationTab}
        />}

        {/* -- USER Infos Buttons -- */}
        {!proposalList && <UndoRedoButtons />}
        <HistoryPanel />
        <PresenceBar />
        {user && <UserInfosComponent />}
        <HelpButton />
      </div>
    </Header >
  );
};

export default React.memo(Navbar);
